/** @jsx jsx */
import { jsx } from "theme-ui";
import React, { useEffect } from "react";
import { Icon } from "@shared-components/Icon/Icon";
import Facebook from "@svg-icons/facebook.component.svg";
import Instagram from "@svg-icons/instagram.component.svg";
import YouTube from "@svg-icons/youtube.component.svg";
import Twitter from "@svg-icons/twitter.component.svg";
import Linkedin from "@svg-icons/linkedin.component.svg";
import HOCIconLogo from "@svg-icons/hoc-icon-logo.component.svg";
import { ButtonLink } from "@shared-components/Button/Button";
import { Box } from "@shared-components/Box/Box";
import { Link } from "@shared-components/Link/Link";
import {
  containerStyles,
  linkListStyles,
  linkStyles,
  soMeLinkStyles,
  soMeWrapStyles,
  sloganWrapStyles,
  iconLogoStyles,
  centerContainerWrapStyles,
} from "./styles";
import {
  LinkCollection,
  LinkCollectionLink,
} from "../../carlsberg-headless/types/LinkCollection";
import { SHORT_CODES_MAP } from "../../carlsberg-headless/shortcodes-map";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { generateCbKey } from "../../shared/utils/generate-cbKey";
import { FlexCell, FlexRow } from "@shared-components/FlexGrid/FlexGrid";
import { ManageCookiesButton } from "../../carlsberg-headless/CompliancePack";
import { getLocalizedUrl } from "../../shared/utils/get-localized-url";

export interface FooterProps {
  linkCollection1stColumn: LinkCollection;
  linkCollection2ndColumn: LinkCollection;
  linkCollection3rdColumn: LinkCollection;
  socialLinks?: LinkCollection;
  cultureSloganStart?: string;
  cultureSloganEnd?: string;
}

const getSocialIcon = (shortCode: string): React.ReactElement | null => {
  switch (shortCode) {
    case SHORT_CODES_MAP.icons.icon_facebook:
      return <Icon svg={Facebook} variant={`footer._facebook`} />;
    case SHORT_CODES_MAP.icons.icon_instagram:
      return <Icon svg={Instagram} variant={`footer._instagram`} />;
    case SHORT_CODES_MAP.icons.icon_youtube:
      return <Icon svg={YouTube} variant={`footer._youtube`} />;
    case SHORT_CODES_MAP.icons.icon_twitter:
      return <Icon svg={Twitter} variant={`footer._twitter`} />;
    case SHORT_CODES_MAP.icons.icon_linkedin:
      return (
        <Icon
          svg={Linkedin}
          variant={`footer._linkedin`}
          sx={{ width: "34px", height: "34px" }}
        />
      );
    default:
      return null;
  }
};

const LinkCollectionColumn: React.FC<{ links: LinkCollectionLink[] }> = ({
  links,
}) => {
  const { currentCulture } = useSiteContext();

  if (!links || links.length < 1) return null;

  return (
    <Box as="ul" sx={linkListStyles}>
      {links.map(({ link }) => (
        <li key={generateCbKey()}>
          <Link
            sx={linkStyles}
            to={getLocalizedUrl(link, currentCulture)}
            {...(link.target && { target: link.target })}
          >
            {link.name}
          </Link>
        </li>
      ))}
    </Box>
  );
};

const ManageCookiesButtonWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ManageCookiesButton>{children}</ManageCookiesButton>;

const PureFooter: React.FC<FooterProps> = ({
  linkCollection1stColumn,
  linkCollection2ndColumn,
  linkCollection3rdColumn,
  socialLinks,
  cultureSloganEnd,
  cultureSloganStart,
}) => {
  const { currentCulture } = useSiteContext();
  const { get } = useDictionaryContext();

  useEffect(() => {
    const tripAdvisorSrc =
      currentCulture === "en-us"
        ? "https://www.jscache.com/wejs?wtype=cdsratingsonlywide&amp;uniq=131&amp;locationId=26830644&amp;lang=en_US&amp;border=false&amp;shadow=false&amp;display_version=2"
        : "https://www.jscache.com/wejs?wtype=cdsratingsonlywide&amp;uniq=28&amp;locationId=26830644&amp;lang=da&amp;border=false&amp;shadow=false&amp;display_version=2";

    const createScript = (src: string, id: string) => {
      const script = document.createElement("script");
      script.src = src;
      script.id = id;
      return script;
    };

    const appendScript = (script: HTMLScriptElement) => {
      document.body.appendChild(script);
    };

    const removeScript = (id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }
    };

    removeScript("tp-script");
    const tripadvisorScript = createScript(tripAdvisorSrc, "tp-script");

    tripadvisorScript.onload = () => {
      Object.keys(window).forEach((key: string) => {
        if (key.match(/^injectcdsratingsonlywide[0-9]+$/)) {
          // @ts-ignore-next-line
          window[key]();
        }
      });
    };

    appendScript(tripadvisorScript);

    return () => {
      removeScript("tp-script");
    };
  }, [currentCulture]);

  return (
    <Box sx={containerStyles}>
      {socialLinks && socialLinks.links.length > 0 && (
        <Box sx={soMeWrapStyles}>
          {socialLinks.links.map(({ link, shortCodes }) => (
            <ButtonLink
              sx={{
                ...soMeLinkStyles,
                ...(shortCodes &&
                  shortCodes[0] === SHORT_CODES_MAP.icons.icon_linkedin && {
                    p: "0",
                  }),
              }}
              key={generateCbKey()}
              shape="hollowCircle"
              outline
              to={link.url}
              {...(link.target && { target: link.target })}
            >
              {shortCodes && getSocialIcon(shortCodes[0])}
            </ButtonLink>
          ))}
        </Box>
      )}

      <Box sx={centerContainerWrapStyles}>
        <FlexRow sx={{ maxWidth: "1050px", width: "full" }} spacing={[4]}>
          <FlexCell col={[12, 4]}>
            <LinkCollectionColumn links={linkCollection1stColumn?.links} />
          </FlexCell>
          <FlexCell col={[12, 4]}>
            <LinkCollectionColumn links={linkCollection2ndColumn?.links} />
          </FlexCell>
          <FlexCell col={[12, 4]}>
            <Box sx={{ textAlign: ["center", "left"] }}>
              <ManageCookiesButtonWrapper>
                {get(
                  DICTIONARY_MAP.global_compliance
                    .global_compliance_manage_cookies_button,
                  currentCulture
                )}
              </ManageCookiesButtonWrapper>
            </Box>
            <LinkCollectionColumn links={linkCollection3rdColumn?.links} />
          </FlexCell>
        </FlexRow>
      </Box>
      <Box sx={{ ...centerContainerWrapStyles, ...{ pt: 6 } }}>
        {currentCulture === "en-us" ? (
          <React.Fragment>
            <div
              id="TA_cdsratingsonlynarrow689"
              className="TA_cdsratingsonlynarrow"
            >
              <ul id="Ni7Yq0" className="TA_links s19Uw6ay">
                <li id="AqkPPxS" className="Gs0aK2uepa">
                  <a
                    target="_blank"
                    href="Home of Carlsberg - All You Need to Know BEFORE You Go (2025) "
                  >
                    <img
                      src="https://www.tripadvisor.com/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-18034-2.svg"
                      alt="TripAdvisor"
                      width={"220px"}
                    />
                  </a>
                </li>
              </ul>
            </div>
            {/* <div
              id="TA_cdsratingsonlywide131"
              className="TA_cdsratingsonlywide"
            >
              <ul id="5hV3eqSu" className="TA_links rZKCqGkBlf9">
                <li id="m2qXmJ5f" className="0oamJRN6OjF">
                  <a
                    target="_blank"
                    href="https://www.tripadvisor.com/Attraction_Review-g189541-d26830644-Reviews-Home_of_Carlsberg-Copenhagen_Zealand.html"
                  >
                    <img
                      src="https://www.tripadvisor.com/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-18034-2.svg"
                      alt="TripAdvisor"
                      width={"220px"}
                    />
                  </a>
                </li>
              </ul>
            </div> */}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div
              id="TA_cdsratingsonlynarrow222"
              className="TA_cdsratingsonlynarrow"
            >
              <ul id="ax4L2kDCR2L" className="TA_links gXBWtx">
                <li id="Aq5KkUPAD0" className="vjJkGvl8">
                  <a
                    target="_blank"
                    href="Home of Carlsberg (København) - Alt du skal vide før du besøger - Tripadvisor "
                  >
                    <img
                      src="https://www.tripadvisor.dk/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-18034-2.svg"
                      alt="TripAdvisor"
                      width={"220px"}
                    />
                  </a>
                </li>
              </ul>
            </div>
            {/* <div id="TA_cdsratingsonlywide28" className="TA_cdsratingsonlywide">
              <ul id="3Kcpr6" className="TA_links SnK2qG3kNCT">
                <li id="7TZhYjYi" className="JxjEtCR77IU">
                  <a
                    target="_blank"
                    href="https://www.tripadvisor.dk/Attraction_Review-g189541-d26830644-Reviews-Home_of_Carlsberg-Copenhagen_Zealand.html"
                  >
                    <img
                      src="https://www.tripadvisor.dk/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-18034-2.svg"
                      alt="TripAdvisor"
                      width={"220px"}
                    />
                  </a>
                </li>
              </ul>
            </div> */}
          </React.Fragment>
        )}
      </Box>
      <Box sx={centerContainerWrapStyles}>
        <Box sx={sloganWrapStyles}>
          {cultureSloganStart && <div>{cultureSloganStart}</div>}
          <Icon sx={iconLogoStyles} svg={HOCIconLogo} />
          {cultureSloganEnd && <div>{cultureSloganEnd}</div>}
        </Box>
      </Box>
    </Box>
  );
};

const Footer: React.FC<
  Omit<FooterProps, "cultureSloganStart" | "cultureSloganEnd">
> = ({
  socialLinks,
  linkCollection1stColumn,
  linkCollection2ndColumn,
  linkCollection3rdColumn,
}) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  return (
    <PureFooter
      socialLinks={socialLinks}
      linkCollection1stColumn={linkCollection1stColumn}
      linkCollection2ndColumn={linkCollection2ndColumn}
      linkCollection3rdColumn={linkCollection3rdColumn}
      cultureSloganStart={get(
        DICTIONARY_MAP.footer.footer_probably_the_best,
        currentCulture
      )}
      cultureSloganEnd={get(
        DICTIONARY_MAP.footer.footer_beer_in_the_world,
        currentCulture
      )}
    />
  );
};

export { Footer, PureFooter };
